/**
 * ChangeAnalysisToolbar — run trigger + importance distribution (T4).
 *
 * lawyer/reviewer get the 「发起变更分析」 button (one click runs both F7 + F8);
 * viewer sees the distribution only (read-only). The distribution counts the
 * *current* importance verdict list by level so the operator sees coverage at a
 * glance. Mirrors F6-6's VerificationToolbar.
 */
import * as React from 'react';
import type { ImportanceLevel, ImportanceResult } from '../../lib/veridoc-api';

import { IMPORTANCE_LABELS, normalizeImportance } from './constants';
import { IMPORTANCE_TONE, TONE_CLASSES } from './importanceVisual';

// Display order — most actionable first (substantive → unclear → wording).
const IMPORTANCE_ORDER: ImportanceLevel[] = ['substantive', 'unclear', 'wording'];

function distribution(results: ImportanceResult[]): Record<ImportanceLevel, number> {
  const counts: Record<ImportanceLevel, number> = {
    substantive: 0,
    wording: 0,
    unclear: 0,
  };
  for (const r of results) counts[normalizeImportance(r.payload.importance)] += 1;
  return counts;
}

export function ChangeAnalysisToolbar({
  results,
  degradedCount,
  running,
  canRun,
  hasAnalyzed,
  onRun,
}: {
  results: ImportanceResult[];
  /** Count of changes that degraded in the last F7 run (LLM unreachable). */
  degradedCount: number;
  running: boolean;
  canRun: boolean;
  /** True once the comparison has any persisted analysis (controls button copy). */
  hasAnalyzed: boolean;
  onRun: () => void;
}): React.JSX.Element {
  const counts = distribution(results);
  const hasResults = results.length > 0;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink-200 bg-ink-0 px-4 py-3 shadow-sm">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="text-sm font-semibold text-ink-900">变更分析</h2>
        {hasResults ? (
          <>
            <span className="text-xs text-ink-500">共 {results.length} 处分类</span>
            <span className="text-ink-300">·</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {IMPORTANCE_ORDER.filter((level) => counts[level] > 0).map((level) => (
                <span
                  key={level}
                  className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs ${
                    TONE_CLASSES[IMPORTANCE_TONE[level]].pill
                  }`}
                >
                  {IMPORTANCE_LABELS[level]} {counts[level]}
                </span>
              ))}
            </div>
          </>
        ) : (
          <span className="text-xs text-ink-500">尚无分类结论</span>
        )}
        {degradedCount > 0 && (
          <span className="text-xs text-semantic-warning">· {degradedCount} 处降级</span>
        )}
      </div>

      {canRun && (
        <button
          type="button"
          onClick={onRun}
          disabled={running}
          className="inline-flex h-btn-sm shrink-0 items-center gap-1.5 rounded-sm border border-brand-600 bg-brand-600 px-3 text-xs font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {running ? (
            <>
              <Spinner />
              正在分析…
            </>
          ) : (
            <>
              <span aria-hidden="true">⟳</span>
              {hasAnalyzed ? '重新分析' : '发起变更分析'}
            </>
          )}
        </button>
      )}
    </div>
  );
}

function Spinner(): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white"
    />
  );
}

export default ChangeAnalysisToolbar;
